import React, { useState, useEffect, useRef } from 'react';
import { User, Check, X, Loader, Camera } from 'lucide-react';

const ProfileSetup = ({
  currentUser,
  userProfile,
  supabase,
  onProfileCreated,
  onClose,
  isEditing = false,
}) => {
  const [formData, setFormData] = useState({
    username: '',
    bio: '',
    location: '',
    seller_terms: 'Standard marketplace terms apply. Contact seller for specific details.',
    is_public: true,
    show_collection_count: true,
    avatar_url: '',
    email_new_messages: true,
    email_admin_announcements: true,
    email_marketing: true,
  });

  const [originalUsername, setOriginalUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(''); // 'checking', 'available', 'taken', 'invalid'
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Load existing profile data when editing
  useEffect(() => {
    const loadProfile = async () => {
      if (isEditing && currentUser) {
        let profileData = userProfile;

        // If userProfile is not provided, fetch it
        if (!profileData) {
          try {
            const { data, error } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', currentUser.id)
              .single();

            if (!error && data) {
              profileData = data;
            }
          } catch (err) {
            console.error('Error loading profile:', err);
          }
        }

        if (profileData) {
          setFormData({
            username: profileData.username || '',
            bio: profileData.bio || '',
            location: profileData.location || '',
            seller_terms:
              profileData.seller_terms ||
              'Standard marketplace terms apply. Contact seller for specific details.',
            is_public: profileData.is_public ?? true,
            show_collection_count: profileData.show_collection_count ?? true,
            avatar_url: profileData.avatar_url || '',
            email_new_messages: profileData.email_new_messages ?? true,
            email_admin_announcements: profileData.email_admin_announcements ?? true,
            email_marketing: profileData.email_marketing ?? true,
          });
          setOriginalUsername(profileData.username || '');
        }
      }
    };

    loadProfile();
  }, [isEditing, userProfile, currentUser, supabase]);

  // Check username availability as user types
  const checkUsername = async username => {
    if (!username || username.length < 3) {
      setUsernameStatus('');
      return;
    }

    // If editing and username hasn't changed, it's valid
    if (isEditing && username === originalUsername) {
      setUsernameStatus('available');
      return;
    }

    // Validate format
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setUsernameStatus('invalid');
      setErrors(prev => ({
        ...prev,
        username: 'Username can only contain letters, numbers, hyphens, and underscores',
      }));
      return;
    }

    setUsernameStatus('checking');
    setErrors(prev => ({ ...prev, username: '' }));

    try {
      const { data, error } = await supabase.rpc('is_username_available', {
        check_username: username,
      });

      if (error) {
        console.error('Username check error:', error);
        setUsernameStatus('');
        return;
      }

      if (data) {
        setUsernameStatus('available');
      } else {
        setUsernameStatus('taken');
        setErrors(prev => ({ ...prev, username: 'Username is already taken' }));
      }
    } catch (err) {
      console.error('Username check failed:', err);
      setUsernameStatus('');
    }
  };

  // Debounced username check
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username) {
        checkUsername(formData.username);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.username]);

  // Handle profile image upload
  const handleImageUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Please select an image file' }));
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image must be less than 5MB' }));
      return;
    }

    setUploadingImage(true);
    setErrors(prev => ({ ...prev, image: '' }));

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser.id}_${Date.now()}.${fileExt}`;
      const filePath = `profile-images/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('media-images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage.from('media-images').getPublicUrl(filePath);

      const profileImageUrl = urlData.publicUrl;

      // Update form data
      setFormData(prev => ({ ...prev, avatar_url: profileImageUrl }));
    } catch (err) {
      console.error('Error uploading profile image:', err);
      setErrors(prev => ({ ...prev, image: 'Failed to upload image. Please try again.' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate required fields
    const newErrors = {};
    if (!formData.username) newErrors.username = 'Username is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check username availability (allow unchanged usernames when editing)
    if (!isEditing || formData.username !== originalUsername) {
      if (usernameStatus !== 'available') {
        setErrors(prev => ({ ...prev, username: 'Please choose a valid, available username' }));
        return;
      }
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const profileData = {
        username: formData.username.toLowerCase(),
        bio: formData.bio || null,
        location: formData.location || null,
        seller_terms: formData.seller_terms || null,
        is_public: formData.is_public,
        show_collection_count: formData.show_collection_count,
        avatar_url: formData.avatar_url || null,
        email_new_messages: formData.email_new_messages,
        email_admin_announcements: formData.email_admin_announcements,
        email_marketing: formData.email_marketing,
      };

      let data, error;

      if (isEditing) {
        // Update existing profile
        ({ data, error } = await supabase
          .from('user_profiles')
          .update({
            ...profileData,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', currentUser.id)
          .select()
          .single());
      } else {
        // Create new profile
        ({ data, error } = await supabase
          .from('user_profiles')
          .insert([
            {
              ...profileData,
              user_id: currentUser.id,
            },
          ])
          .select()
          .single());
      }

      if (error) {
        if (error.code === '23505') {
          setErrors({ username: 'Username is already taken' });
        } else {
          setErrors({
            general: `Failed to ${isEditing ? 'update' : 'create'} profile: ` + error.message,
          });
        }
        return;
      }

      console.log(`Profile ${isEditing ? 'updated' : 'created'} successfully:`, data);
      onProfileCreated(data);

      if (isEditing && onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Profile creation error:', err);
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUsernameIcon = () => {
    switch (usernameStatus) {
      case 'checking':
        return <Loader className="animate-spin text-gray-400" size={16} />;
      case 'available':
        return <Check className="text-green-500" size={16} />;
      case 'taken':
      case 'invalid':
        return <X className="text-red-500" size={16} />;
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      onClick={e => {
        // Close modal if clicking on overlay (not the modal content)
        if (e.target === e.currentTarget && isEditing && onClose) {
          onClose();
        }
      }}
    >
      <div
        className="bg-black border border-gray-600 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto dark-scrollbar"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          {isEditing && (
            <div className="flex justify-end mb-4">
              <button onClick={onClose} className="text-gray-400 hover:text-red-500" type="button">
                <X size={24} />
              </button>
            </div>
          )}
          <div className="text-center mb-6">
            {/* Profile Picture moved to header */}
            <div className="flex justify-center mb-4">
              <div
                className="relative w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center border-2 border-gray-600 cursor-pointer hover:border-red-500 transition-colors overflow-hidden group"
                onClick={handleAvatarClick}
              >
                {formData.avatar_url ? (
                  <img
                    src={formData.avatar_url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={32} className="text-gray-300" />
                )}
                {uploadingImage && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <Loader className="animate-spin text-white" size={20} />
                  </div>
                )}
                {!uploadingImage && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all">
                    <Camera
                      size={20}
                      className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                )}
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white">
              {isEditing ? 'Edit Profile' : 'Complete Your Profile'}
            </h2>
            <p className="text-gray-300 mt-2">
              {isEditing
                ? 'Update your profile information'
                : 'Set up your username to get the full picture'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />

            {/* Error display for image upload */}
            {errors.image && (
              <div className="text-center">
                <p className="text-red-600 text-xs">{errors.image}</p>
              </div>
            )}

            {/* Username Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Username *
                {isEditing && (
                  <span className="text-xs text-gray-400 ml-2">(cannot be changed)</span>
                )}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 sm:text-sm">@</span>
                </div>
                <input
                  type="text"
                  value={formData.username}
                  onChange={e => setFormData({ ...formData, username: e.target.value })}
                  className={`block w-full pl-8 pr-10 py-2 border rounded-lg bg-black text-white hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${
                    errors.username ? 'border-red-500' : 'border-gray-600'
                  } ${isEditing ? 'bg-black cursor-not-allowed hover:border-gray-600' : ''}`}
                  placeholder="your_username"
                  maxLength={30}
                  required
                  disabled={isEditing}
                  readOnly={isEditing}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {!isEditing && getUsernameIcon()}
                </div>
              </div>
              {errors.username && <p className="text-red-600 text-sm mt-1">{errors.username}</p>}
              {!isEditing && (
                <p className="text-gray-400 text-xs mt-1">
                  3-30 characters, letters, numbers, hyphens, and underscores only
                </p>
              )}
            </div>

            {/* Bio Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Bio</label>
              <textarea
                value={formData.bio}
                onChange={e => setFormData({ ...formData, bio: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                placeholder="Tell us about your VHS collection..."
                rows={3}
                maxLength={500}
              />
              <p className="text-gray-400 text-xs mt-1">{formData.bio.length}/500 characters</p>
            </div>

            {/* Location Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={e => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                placeholder="City, State/Country"
                maxLength={100}
              />
            </div>

            {/* Seller Terms Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Seller Terms</label>
              <textarea
                value={formData.seller_terms}
                onChange={e => setFormData({ ...formData, seller_terms: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                placeholder="Enter your selling terms (shipping, returns, payment, etc.)"
                rows={4}
                maxLength={1000}
              />
              <p className="text-gray-400 text-xs mt-1">
                {formData.seller_terms.length}/1000 characters
              </p>
            </div>

            {/* Privacy Settings */}
            <div className="space-y-3 pt-4 border-t border-gray-600">
              <h3 className="text-sm font-medium text-white">Privacy Settings</h3>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_public}
                  onChange={e => setFormData({ ...formData, is_public: e.target.checked })}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <span className="text-sm font-medium text-white">Public Profile</span>
                  <p className="text-xs text-gray-400">
                    Allow others to view your profile and collection
                  </p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_collection_count}
                  onChange={e =>
                    setFormData({ ...formData, show_collection_count: e.target.checked })
                  }
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <span className="text-sm font-medium text-white">Show Collection Stats</span>
                  <p className="text-xs text-gray-400">
                    Display your collection count on your profile
                  </p>
                </div>
              </label>
            </div>

            {/* Email Notification Settings */}
            <div className="space-y-3 pt-4 border-t border-gray-600">
              <div>
                <h3 className="text-sm font-medium text-white">Email Notifications</h3>
                <p className="text-xs text-gray-500 mt-1">Email notifications are coming soon.</p>
              </div>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.email_new_messages}
                  onChange={e => setFormData({ ...formData, email_new_messages: e.target.checked })}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <span className="text-sm font-medium text-white">New Messages</span>
                  <p className="text-xs text-gray-400">
                    Get emailed when someone sends you a message, marketplace inquiry, or trade
                    request
                  </p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.email_admin_announcements}
                  onChange={e =>
                    setFormData({ ...formData, email_admin_announcements: e.target.checked })
                  }
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <span className="text-sm font-medium text-white">Site Announcements</span>
                  <p className="text-xs text-gray-400">
                    Receive important updates and announcements from Rewind Archive
                  </p>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.email_marketing}
                  onChange={e => setFormData({ ...formData, email_marketing: e.target.checked })}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <span className="text-sm font-medium text-white">Newsletter</span>
                  <p className="text-xs text-gray-400">Occasional newsletter emails</p>
                </div>
              </label>
            </div>

            {/* Error Display */}
            {errors.general && (
              <div className="bg-red-900 border border-red-500 rounded-lg p-3">
                <p className="text-red-300 text-sm">{errors.general}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={
                isSubmitting ||
                (!isEditing && (usernameStatus !== 'available' || !formData.username))
              }
              className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  <span>{isEditing ? 'Updating...' : 'Creating Profile...'}</span>
                </>
              ) : (
                <span>{isEditing ? 'Update Profile' : 'Create Profile'}</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
